import Cookies from 'js-cookie';
// Types
import { Token } from 'context/AppContext';
// Helpers
import { isJsonString } from 'helpers/textEditor';
import {
  getCookiesDomain,
  getCookiesTokenEnvName,
  isDevelopmentEnv,
} from './env';

export const TOKEN_NAME = 'token';
export const GUEST_TOKEN_NAME = 'guest-token';
export const COHOST_TOKEN_NAME = 'cohost-token';

const cookieOptions = {
  domain: getCookiesDomain(),
  sameSite: 'Lax' as const,
  secure: !isDevelopmentEnv,
  expires: 90,
};

const setItem = (key: string, value: string): void => {
  Cookies.set(key, value, cookieOptions);
};

const getItem = (key: string): string | null => {
  return Cookies.get(key) ?? null;
};

const removeItem = (key: string): void => {
  Cookies.remove(key, cookieOptions);
};

// Token
export const setTokenToCookies = (token: Token): void => {
  setItem(`${TOKEN_NAME}-${getCookiesTokenEnvName()}`, JSON.stringify(token));
};

export const getTokenFromCookies = (): Token | null => {
  const token = getItem(`${TOKEN_NAME}-${getCookiesTokenEnvName()}`);

  return token && isJsonString(token) ? JSON.parse(token) : null;
};

export const clearTokenFromCookies = (): void => {
  removeItem(`${TOKEN_NAME}-${getCookiesTokenEnvName()}`);
};

// Guest Token
export const setGuestTokenToCookies = (guestToken: Token): void => {
  setItem(
    `${GUEST_TOKEN_NAME}-${getCookiesTokenEnvName()}`,
    JSON.stringify(guestToken)
  );
};

export const getGuestTokenFromCookies = (): Token | null => {
  const guestToken = getItem(`${GUEST_TOKEN_NAME}-${getCookiesTokenEnvName()}`);

  return guestToken && isJsonString(guestToken) ? JSON.parse(guestToken) : null;
};

export const clearGuestTokenFromCookies = (): void => {
  removeItem(`${GUEST_TOKEN_NAME}-${getCookiesTokenEnvName()}`);
};

// Cohost token
export const setCohostTokenToCookies = (token: string): void => {
  setItem(`${COHOST_TOKEN_NAME}-${getCookiesTokenEnvName()}`, token);
};

export const getCohostTokenFromCookies = (): string | null => {
  return getItem(`${COHOST_TOKEN_NAME}-${getCookiesTokenEnvName()}`);
};

export const clearCohostTokenFromCookies = (): void => {
  removeItem(`${COHOST_TOKEN_NAME}-${getCookiesTokenEnvName()}`);
};

// Get one of existing token
export const getCohostOrRegularAccessTokenFromCookies = (): string | null => {
  return (
    getCohostTokenFromCookies() ||
    getTokenFromCookies()?.accessToken ||
    getGuestTokenFromCookies()?.accessToken ||
    null
  );
};

// Stream affiliate attribution (last click wins, one cookie per stream)
export const AFFILIATE_COOKIE_PREFIX = 'aff_';
export const AFFILIATE_COOKIE_MAX_AGE_DAYS = 30;

export const getAffiliateCookieName = (streamId: string): string =>
  `${AFFILIATE_COOKIE_PREFIX}${streamId}`;

export const setAffiliateCodeToCookies = (
  streamId: string,
  code: string
): void => {
  Cookies.set(getAffiliateCookieName(streamId), code, {
    ...cookieOptions,
    expires: AFFILIATE_COOKIE_MAX_AGE_DAYS,
  });
};

export const getAffiliateCodeFromCookies = (streamId: string): string | null =>
  getItem(getAffiliateCookieName(streamId));

export const clearAffiliateCodeFromCookies = (streamId: string): void => {
  removeItem(getAffiliateCookieName(streamId));
};

/** Same cookie as `setAffiliateCodeToCookies`, as a `Set-Cookie` header value for the SSR landing route. */
export const getAffiliateSetCookieHeader = (
  streamId: string,
  code: string
): string => {
  const parts = [
    `${getAffiliateCookieName(streamId)}=${encodeURIComponent(code)}`,
    'Path=/',
    `Max-Age=${AFFILIATE_COOKIE_MAX_AGE_DAYS * 24 * 60 * 60}`,
    `SameSite=${cookieOptions.sameSite}`,
  ];

  if (cookieOptions.domain) {
    parts.push(`Domain=${cookieOptions.domain}`);
  }

  if (cookieOptions.secure) {
    parts.push('Secure');
  }

  return parts.join('; ');
};

// Cookie Consent
export const COOKIE_CONSENT_NAME = 'cookie-consent';

export const setCookieConsentToCookies = (): void => {
  Cookies.set(
    `${COOKIE_CONSENT_NAME}-${getCookiesTokenEnvName()}`,
    'accepted',
    { ...cookieOptions, expires: 365 }
  );
};

export const getCookieConsentFromCookies = (): boolean => {
  return (
    getItem(`${COOKIE_CONSENT_NAME}-${getCookiesTokenEnvName()}`) === 'accepted'
  );
};
